import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { baseService } from "../base";
import { PaginationResponse } from "../type";
import {
  CreateSupplyKitParams,
  GetSupplyKitsParams,
  SupplyKit,
  UpdateSupplyKitParams,
} from "./supply-kit.types";

// Query Keys
export const SUPPLY_KIT_KEYS = {
  GET_SUPPLY_KITS: (params: GetSupplyKitsParams) => ["supply-kits", params],
  GET_SUPPLY_KIT: (id: string) => ["supply-kit", id],
};

// Get all supply kits
export const useGetSupplyKits = (
  params: GetSupplyKitsParams,
  options?: UseQueryOptions<PaginationResponse<SupplyKit>>
) => {
  return useQuery<PaginationResponse<SupplyKit>>({
    queryKey: SUPPLY_KIT_KEYS.GET_SUPPLY_KITS(params),
    queryFn: async () => {
      const { data } = await baseService.get<PaginationResponse<SupplyKit>>(
        "/supply-kits",
        { params }
      );
      return data;
    },
    ...options,
  });
};

// Get single supply kit
export const useGetSupplyKit = (
  id?: string,
  options?: UseQueryOptions<SupplyKit>
) => {
  return useQuery<SupplyKit>({
    queryKey: SUPPLY_KIT_KEYS.GET_SUPPLY_KIT(id ?? ""),
    queryFn: async () => {
      const { data } = await baseService.get<SupplyKit>(`/supply-kits/${id}`);
      return data;
    },
    enabled: !!id,
    ...options,
  });
};

// Create supply kit
export const useCreateSupplyKit = () => {
  const queryClient = useQueryClient();

  return useMutation<SupplyKit, Error, CreateSupplyKitParams>({
    mutationFn: async (params) => {
      const { data } = await baseService.post<SupplyKit>("/supply-kits", params);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supply-kits"] });
    },
  });
};

// Update supply kit
export const useUpdateSupplyKit = () => {
  const queryClient = useQueryClient();

  return useMutation<
    SupplyKit,
    Error,
    { id: string } & UpdateSupplyKitParams
  >({
    mutationFn: async ({ id, ...params }) => {
      const { data } = await baseService.patch<SupplyKit>(`/supply-kits/${id}`, params);
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["supply-kits"] });
      queryClient.invalidateQueries({
        queryKey: SUPPLY_KIT_KEYS.GET_SUPPLY_KIT(variables.id),
      });
    },
  });
};

// Delete supply kit
export const useDeleteSupplyKit = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await baseService.delete(`/supply-kits/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supply-kits"] });
    },
  });
};
